import React, { useState } from "react";
import { styled } from "../../stitches.config";
import * as Yup from "yup";
import { Formik, Form, Field } from 'formik';


import SuccessIcon from "../assets/icons/success-icon.svg";
import { body, bodyLarge, h3 } from "../commonStyles/Fonts";
import { useAuth } from "../../context/AuthContext";
import { UserDeleteActionType } from "../../api/account/delete";

interface IDeleteAccountModalProps {
    onCloseBtn: () => void;
    onDeleteBtn: () => Promise<UserDeleteActionType>;
    open: boolean;
}

type FormValues = {
    email: string,
}

const DeleteAccountModal: React.FC<IDeleteAccountModalProps> = ({ onCloseBtn, onDeleteBtn, open }) => {

    const [{ user }, dispatch] = useAuth();

    const [submitting, setSubmitting] = useState<boolean>(false)
    const [deleteAction, setDeleteAction] = useState<UserDeleteActionType | undefined>(undefined)
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const initialValues: FormValues = {
        email: "",
    }
    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .matches(new RegExp(`^(${user?.email})$`), "Wprowadzono nieprawidłową wartość")
            .required("Pole wymagane"),
    })

    const handleSubmit = ({ email }: FormValues, { setErrors }: { setErrors: any }) => {
        if (submitting) return;

        setSubmitting(true);
        onDeleteBtn().then((action: UserDeleteActionType) => {
            setDeleteAction(action);
        }).finally(() => {
            setSubmitting(false);
        });
    };
    const togglePasswordVisible = () => setPasswordVisible((prev) => !prev);

    const closeModal = () => {
        if (deleteAction == "DELETE_NOW") {
            dispatch({ type: "logout" });
            localStorage.removeItem("access_token");
            return;
        }
        if (!submitting)
            return onCloseBtn();
    }

    if (!open) return (<></>);

    return (
        <Wrapper>
            <Box>
                <Header className={h3()}>
                    <p>
                        {!deleteAction && "Czy na pewno chcesz usunąć swoje konto?"}
                        {deleteAction == "DELETE_NOW" && <><span></span> Konto zostało usunięte</>}
                        {deleteAction == "DELETE_AFTER_EXPIRATION" && <><span></span> Konto zostanie usunięte</>}
                    </p>
                    <CloseButton onClick={closeModal}>
                        <svg viewBox="0 0 20 21" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M11.7525 10.4142L19.5455 2.62119L17.707 0.782715L9.91401 8.57568L2.12119 0.782868L0.282715 2.62135L8.07553 10.4142L0.282715 18.207L2.12119 20.0455L9.91401 12.2526L17.707 20.0456L19.5455 18.2071L11.7525 10.4142ZM9.91401 8.99995L17.707 1.20698L17.707 1.20703L9.91406 9L9.91401 8.99995ZM2.12119 1.20713L2.12124 1.20718L0.707031 2.6214L0.706979 2.62135L2.12119 1.20713ZM8.4998 10.4142L0.706979 18.207L0.707031 18.207L8.49985 10.4142L8.4998 10.4142Z" />
                        </svg>
                    </CloseButton>
                </Header>
                <Description className={bodyLarge()}>
                    {!deleteAction && (
                        <>
                            Usunięcie konta spowoduje:
                            <br />
                            -usunięcie wszystkich ustawień
                            <br />
                            -wypisanie z newslettera
                        </>
                    )}
                    {deleteAction == "DELETE_NOW" && (
                        <>
                            Gdybyś chciał kiedyś do nas wrócić, możesz zawsze założyć konto jeszcze raz. Będziemy na Ciebie czekać
                        </>
                    )}
                    {deleteAction == "DELETE_AFTER_EXPIRATION" && (
                        <>
                            Posiadasz aktywną subskrypcję usługi "Twoje naTemat", więc Twoje konto zostanie usunięte w momencie jej zakończenia - {user?.valid_to}
                        </>
                    )}
                </Description>
                {!deleteAction && (
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}>
                        {({ errors, touched, status }) => (
                            <Form className={body()}>
                                <label
                                    className={`${errors.email && touched.email && "show-error-input-text"
                                        }`}
                                    htmlFor="email"
                                >
                                    Wpisz <strong>{user?.email}</strong>, aby usunąć konto
                                </label>
                                <PasswordWrapper>
                                    <Field
                                        type={"text"}
                                        name="email"
                                        className={`${bodyLarge()} ${errors.email &&
                                            touched.email &&
                                            "show-error-input-border"
                                            }`}
                                        placeholder="Wpisz swój email"
                                        disabled={submitting}
                                    />
                                </PasswordWrapper>
                                {errors.email && touched.email && (
                                    <span className="validationError">{errors.email}</span>
                                )}
                                <Field
                                    className="btn-red"
                                    type="submit"
                                    value="Usuń konto"
                                    disabled={submitting}
                                />
                            </Form>
                        )}
                    </Formik>
                )}
                <div className={bodyLarge()}>
                    <button className={!deleteAction ? "btn-white" : "btn-black"} onClick={closeModal}>
                        {!deleteAction ? "Anuluj" : "Zamknij"}
                    </button>
                </div>
            </Box>
        </Wrapper>
    );
};

const Wrapper = styled("div", {
    position: "fixed",
    zIndex: "2000000",
    background: "rgba(0,0,0,0.75)",
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    top: 0,
    left: 0,
    color: "$grayscale100",
});


const Box = styled("div", {
    maxWidth: "408px",
    background: "$grayscale0",
    opacity: "1",
    padding: "32px 16px 40px 16px",
    margin: " 0 20px",
    "@bp3": {
        margin: 0,
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
    },
    "@bp4": {
        padding: "24px 32px 32px 32px",
    },
    "& button": {
        padding: "11px 0 13px 0",
        width: "100%",
        borderWidth: "0px",
    },
    ".btn-red": {
        background: "$aszdziennikPrimary",
        color: "$grayscale0Fixed",
        padding: "11px 0 13px 0",
        width: "100%",
        borderWidth: "0px !important",
        marginTop: "32px",
        fontSize: "16px",
        "&:hover": {
            borderWidth: "0px !important",
        }
    },
    ".btn-white": {
        background: "$grayscale0",
        color: "$grayscale100",
        marginTop: "13px",
    },
    ".btn-black": {
        background: "$grayscale100Fixed",
        color: "$grayscale0Fixed"
    },
    "& form": {
        display: "flex",
        flexDirection: "column",
        "& label": {
            margin: "4px 0 4px 0",
        },
        "& input": {
            border: "1px solid $grayscale25",
            fontWeight: "500",
            padding: "12px 14px",
            transition: "0.2s",
            caretColor: "$primary",
            color: "$grayscale100Fixed",
            "&::placeholder": {
                color: "$grayscale50",
            },
            "&:hover": {
                border: "1px solid $grayscale50",
            },
            "&:focus": {
                border: "1px solid $grayscale50",
            },
        },
        "& a": {
            marginTop: "6px",
            "& span": {
                fontSize: "12px",
                lineHeight: "16px",
                color: "$grayscale90",
                textDecoration: "underline",
            },
            "& .passwordRecovery": {
                fontWeight: 400,
                letterSpacing: "0.1px",
            },
        },
    },
    "& .validationError": {
        color: "$aszdziennikPrimary",
        fontSize: "12px",
        lineHeight: "16px",
        marginTop: "8px",
    },

    ".show-error-input-border": {
        border: "1px solid $aszdziennikPrimary",
        "&:hover": {
            border: "1px solid $aszdziennikPrimary",
        },
        "&:focus": {
            border: "1px solid $aszdziennikPrimary",
        },
    },

    ".show-error-input-text": {
        color: "$aszdziennikPrimary",
    },
});

const Header = styled("div", {
    display: "flex",
    justifyContent: "space-between",
    "& p": {
        margin: 0,
        maxWidth: "300px",
        fontWeight: "600",
        color: "$grayscale100",
    },
    "& span": {
        display: "block",
        width: "44px",
        height: "44px",
        marginBottom: "24px",
        backgroundImage: `url("${SuccessIcon}")`,
        backgroundSize: "cover",
    }
});

const Description = styled("div", {
    margin: "32px 0",
});


const CloseButton = styled("button", {
    border: "none",
    background: "none",
    width: "16px !important",
    height: "16px",
    padding: "0",
    lineHeight: 0,
    img: {
        width: "100%",
        height: "100%",
    },
    "svg": {
        fill: "$grayscale100",
        width: "16px",
        height: "16px",
    }
});

const PasswordWrapper = styled("div", {
    width: "100%",
    position: "relative",
    input: {
        width: "100%",
    },
    strong: {
        fontWeight: 900
    }
});

const PasswordIcon = styled("div", {
    position: "absolute",
    top: "16px",
    right: "16px",
    cursor: "pointer",
    userSelect: "none",
});


export default DeleteAccountModal;
